import { FullLoader } from "components/FullLoader";
import { SignInPage } from "pages/auth/SignIn.page";
import { Suspense } from "react";
import {
	Navigate,
	Route,
	BrowserRouter as Router,
	Routes,
	useLocation,
} from "react-router-dom";
import { lazyWithRetry } from "utils/lazyWithRetry";
import { CloseTrackerGate } from "./CloseTrackerGate";
import { IntercompanyGate } from "./IntercompanyGate";
import { PmCloseTabsRoute } from "./PmCloseTabsRoute";
import { Protected } from "./Protected";

// Route-level code splitting.
//
// Every page below used to be a static import, which put the entire app —
// the ML admin console, the chat admin, the project-management Gantt/workflow
// suite — into the entry chunk, so the sign-in screen downloaded all of it.
// `SignInPage` and `Protected` stay eager because they are on the critical
// path for the very first render; everything else is fetched on navigation.
//
// Named exports need the `.then(m => ({ default: m.X }))` shim because
// React.lazy resolves a module's default export.
const AccountPage = lazyWithRetry(() =>
	import("pages/account").then((m) => ({ default: m.AccountPage })),
);
const BillingPage = lazyWithRetry(() =>
	import("pages/account/Billing").then((m) => ({ default: m.BillingPage })),
);
const CreditsPage = lazyWithRetry(() =>
	import("pages/account/Credits").then((m) => ({ default: m.CreditsPage })),
);
const PersonalPage = lazyWithRetry(() =>
	import("pages/account/Personal").then((m) => ({ default: m.PersonalPage })),
);
const ChatAdmin = lazyWithRetry(() =>
	import("pages/admin/chat").then((m) => ({ default: m.ChatAdmin })),
);
const Analytics = lazyWithRetry(() =>
	import("pages/admin/chat/Analytics").then((m) => ({ default: m.Analytics })),
);
const Chat = lazyWithRetry(() =>
	import("pages/admin/chat/Chat").then((m) => ({ default: m.Chat })),
);
const General = lazyWithRetry(() =>
	import("pages/admin/chat/General").then((m) => ({ default: m.General })),
);
const Guardrails = lazyWithRetry(() =>
	import("pages/admin/chat/Guardrails").then((m) => ({
		default: m.Guardrails,
	})),
);
const McpTools = lazyWithRetry(() =>
	import("pages/admin/chat/McpTools").then((m) => ({ default: m.McpTools })),
);
const PromptTemplates = lazyWithRetry(() =>
	import("pages/admin/chat/PromptTemplates").then((m) => ({
		default: m.PromptTemplates,
	})),
);
const Schemas = lazyWithRetry(() =>
	import("pages/admin/chat/Schemas").then((m) => ({ default: m.Schemas })),
);
const Sessions = lazyWithRetry(() =>
	import("pages/admin/chat/Sessions").then((m) => ({ default: m.Sessions })),
);
const Sources = lazyWithRetry(() =>
	import("pages/admin/chat/Sources").then((m) => ({ default: m.Sources })),
);
const LiveQueue = lazyWithRetry(() =>
	import("pages/admin/chat/live/LiveQueue").then((m) => ({
		default: m.LiveQueue,
	})),
);
const LiveHandoffDetail = lazyWithRetry(() =>
	import("pages/admin/chat/live/LiveHandoffDetail").then((m) => ({
		default: m.LiveHandoffDetail,
	})),
);
const AdminAccessLogPage = lazyWithRetry(() =>
	import("pages/admin/access-log").then((m) => ({
		default: m.AdminAccessLogPage,
	})),
);
const AdminCompaniesPage = lazyWithRetry(() =>
	import("pages/admin/companies/index").then((m) => ({
		default: m.AdminCompaniesPage,
	})),
);
const AdminCreditPackagesPage = lazyWithRetry(() =>
	import("pages/admin/credit-packages").then((m) => ({
		default: m.AdminCreditPackagesPage,
	})),
);
const FileManagerTab = lazyWithRetry(() =>
	import("pages/admin/file-manager").then((m) => ({
		default: m.FileManagerTab,
	})),
);
const MachineLearningAdmin = lazyWithRetry(() =>
	import("pages/admin/machine-learning").then((m) => ({
		default: m.MachineLearningAdmin,
	})),
);
const MLAdminDashboard = lazyWithRetry(() =>
	import("pages/admin/machine-learning/Dashboard").then((m) => ({
		default: m.MLAdminDashboard,
	})),
);
const DataManagement = lazyWithRetry(() =>
	import("pages/admin/machine-learning/DataManagement").then((m) => ({
		default: m.DataManagement,
	})),
);
const ModelManagement = lazyWithRetry(() =>
	import("pages/admin/machine-learning/ModelManagement").then((m) => ({
		default: m.ModelManagement,
	})),
);
const Monitoring = lazyWithRetry(() =>
	import("pages/admin/machine-learning/Monitoring").then((m) => ({
		default: m.Monitoring,
	})),
);
const SystemConfiguration = lazyWithRetry(() =>
	import("pages/admin/machine-learning/SystemConfiguration").then((m) => ({
		default: m.SystemConfiguration,
	})),
);
const TenantManagement = lazyWithRetry(() =>
	import("pages/admin/machine-learning/TenantManagement").then((m) => ({
		default: m.TenantManagement,
	})),
);
const Training = lazyWithRetry(() =>
	import("pages/admin/machine-learning/Training").then((m) => ({
		default: m.Training,
	})),
);
const AdminRulesPage = lazyWithRetry(() =>
	import("pages/admin/rules/index").then((m) => ({
		default: m.AdminRulesPage,
	})),
);
const AdminServicesPage = lazyWithRetry(() =>
	import("pages/admin/services/index").then((m) => ({
		default: m.AdminServicesPage,
	})),
);
const StaffAccessPage = lazyWithRetry(() =>
	import("pages/admin/staff-access").then((m) => ({
		default: m.StaffAccessPage,
	})),
);
const AdminSupportAccessPage = lazyWithRetry(() =>
	import("pages/admin/support-access").then((m) => ({
		default: m.AdminSupportAccessPage,
	})),
);
const SupportTicketsAdmin = lazyWithRetry(() =>
	import("pages/admin/support-tickets").then((m) => ({
		default: m.SupportTicketsAdmin,
	})),
);
const AutoCloseSettingsPage = lazyWithRetry(() =>
	import("pages/admin/support-tickets/AutoCloseSettings").then((m) => ({
		default: m.AutoCloseSettings,
	})),
);
const Categories = lazyWithRetry(() =>
	import("pages/admin/support-tickets/Categories").then((m) => ({
		default: m.Categories,
	})),
);
const SupportTicketDashboard = lazyWithRetry(() =>
	import("pages/admin/support-tickets/Dashboard").then((m) => ({
		default: m.Dashboard,
	})),
);
const SupportMacrosPage = lazyWithRetry(() =>
	import("pages/admin/support-tickets/Macros").then((m) => ({
		default: m.Macros,
	})),
);
const SlaConfigPage = lazyWithRetry(() =>
	import("pages/admin/support-tickets/SlaConfig").then((m) => ({
		default: m.SlaConfig,
	})),
);
const SpamSettingsPage = lazyWithRetry(() =>
	import("pages/admin/support-tickets/SpamSettings").then((m) => ({
		default: m.SpamSettings,
	})),
);
const TicketDetail = lazyWithRetry(() =>
	import("pages/admin/support-tickets/TicketDetail").then((m) => ({
		default: m.TicketDetail,
	})),
);
const TicketList = lazyWithRetry(() =>
	import("pages/admin/support-tickets/TicketList").then((m) => ({
		default: m.TicketList,
	})),
);
const AdminUsersPage = lazyWithRetry(() =>
	import("pages/admin/users/index").then((m) => ({
		default: m.AdminUsersPage,
	})),
);
const AssistantPage = lazyWithRetry(() =>
	import("pages/assistant").then((m) => ({ default: m.AssistantPage })),
);
const AppSumo = lazyWithRetry(() =>
	import("pages/auth/AppSumo.page").then((m) => ({ default: m.AppSumo })),
);
const ForgotPasswordPage = lazyWithRetry(() =>
	import("pages/auth/Forgot.page").then((m) => ({
		default: m.ForgotPasswordPage,
	})),
);
const ResetPasswordPage = lazyWithRetry(() =>
	import("pages/auth/Reset.page").then((m) => ({
		default: m.ResetPasswordPage,
	})),
);
const SignUpPage = lazyWithRetry(() =>
	import("pages/auth/SignUp.page").then((m) => ({ default: m.SignUpPage })),
);
const VerifyEmailPage = lazyWithRetry(() =>
	import("pages/auth/VerifyEmail.page").then((m) => ({
		default: m.VerifyEmailPage,
	})),
);
const CompanyPage = lazyWithRetry(() =>
	import("pages/company").then((m) => ({ default: m.CompanyPage })),
);
const AnalyticsPage = lazyWithRetry(() =>
	import("pages/company/analytics").then((m) => ({ default: m.AnalyticsPage })),
);
const ReconcilePage = lazyWithRetry(() =>
	import("pages/company/classic_reconcile").then((m) => ({
		default: m.ReconcilePage,
	})),
);
const DashboardPage = lazyWithRetry(() =>
	import("pages/company/dashboard").then((m) => ({ default: m.DashboardPage })),
);
const FilesPage = lazyWithRetry(() =>
	import("pages/company/files").then((m) => ({ default: m.FilesPage })),
);
const ReclassifyPage = lazyWithRetry(() =>
	import("pages/company/reclassify").then((m) => ({
		default: m.ReclassifyPage,
	})),
);
const ReviewPage = lazyWithRetry(() =>
	import("pages/company/review").then((m) => ({ default: m.ReviewPage })),
);
const ReviewHistory = lazyWithRetry(() =>
	import("pages/company/review/ReviewHistory").then((m) => ({
		default: m.ReviewHistory,
	})),
);
const ReviewResult = lazyWithRetry(() =>
	import("pages/company/review/ReviewResult").then((m) => ({
		default: m.ReviewResult,
	})),
);
const ReviewRule = lazyWithRetry(() =>
	import("pages/company/review/ReviewRule").then((m) => ({
		default: m.ReviewRule,
	})),
);
const ReviewRun = lazyWithRetry(() =>
	import("pages/company/review/ReviewRun").then((m) => ({
		default: m.ReviewRun,
	})),
);
const ServicesLayout = lazyWithRetry(() =>
	import("pages/company/services").then((m) => ({ default: m.ServicesLayout })),
);
const ServicesPage = lazyWithRetry(() =>
	import("pages/company/services").then((m) => ({ default: m.ServicesPage })),
);
const ServiceCompletionPage = lazyWithRetry(() =>
	import("pages/company/services//service-completion").then((m) => ({
		default: m.ServiceCompletionPage,
	})),
);
const DocumentsAccess = lazyWithRetry(() =>
	import("pages/company/services/document-access").then((m) => ({
		default: m.DocumentsAccess,
	})),
);
const GrantAccess = lazyWithRetry(() =>
	import("pages/company/services/grant-access").then((m) => ({
		default: m.GrantAccess,
	})),
);
const ScheduleMeeting = lazyWithRetry(() =>
	import("pages/company/services/schedule-meeting").then((m) => ({
		default: m.ScheduleMeeting,
	})),
);
const SettingsPage = lazyWithRetry(() =>
	import("pages/company/settings").then((m) => ({ default: m.SettingsPage })),
);
const AmazonConnectionPage = lazyWithRetry(() =>
	import("pages/connection/AmazonConnection").then((m) => ({
		default: m.AmazonConnectionPage,
	})),
);
const QboConnectionPage = lazyWithRetry(() =>
	import("pages/connection/QboConnection").then((m) => ({
		default: m.QboConnectionPage,
	})),
);
const QboDisconnectHandler = lazyWithRetry(() =>
	import("pages/connection/QboDisconnectHandler").then((m) => ({
		default: m.QboDisconnectHandler,
	})),
);
const QboReconcileRedirector = lazyWithRetry(() =>
	import("pages/connection/QboReconcileRedirector").then((m) => ({
		default: m.QboReconcileRedirector,
	})),
);
const DocumentCenterPage = lazyWithRetry(() =>
	import("pages/document-center").then((m) => ({
		default: m.DocumentCenterPage,
	})),
);
const FileConverterPage = lazyWithRetry(() =>
	import("pages/file-converter").then((m) => ({
		default: m.FileConverterPage,
	})),
);
const CloseTrackerPage = lazyWithRetry(() =>
	import("pages/close/CloseTrackerPage").then((m) => ({
		default: m.CloseTrackerPage,
	})),
);
const CloseAddressResolver = lazyWithRetry(() =>
	import("pages/close/CloseAddressResolver").then((m) => ({
		default: m.CloseAddressResolver,
	})),
);
const ClientCloseSettingsPage = lazyWithRetry(() =>
	import("pages/close/ClientCloseSettingsPage").then((m) => ({
		default: m.ClientCloseSettingsPage,
	})),
);
const CloseTemplatesPage = lazyWithRetry(() =>
	import("pages/close/CloseTemplatesPage").then((m) => ({
		default: m.CloseTemplatesPage,
	})),
);
const IntercompanyPage = lazyWithRetry(() =>
	import("pages/firm/IntercompanyPage").then((m) => ({
		default: m.IntercompanyPage,
	})),
);
const HelpPage = lazyWithRetry(() =>
	import("pages/help").then((m) => ({ default: m.HelpPage })),
);
const AllCompaniesPage = lazyWithRetry(() =>
	import("pages/home/AllCompanies.page").then((m) => ({
		default: m.AllCompaniesPage,
	})),
);
const ProjectManagementPage = lazyWithRetry(() =>
	import("pages/project-management").then((m) => ({
		default: m.ProjectManagementPage,
	})),
);
const SupportPage = lazyWithRetry(() =>
	import("pages/support").then((m) => ({ default: m.SupportPage })),
);
const TicketView = lazyWithRetry(() =>
	import("pages/support/TicketView").then((m) => ({ default: m.TicketView })),
);
const TeamPage = lazyWithRetry(() =>
	import("pages/team").then((m) => ({ default: m.TeamPage })),
);
const ExceptionDashboard = lazyWithRetry(() =>
	import("../pages/company/advanced_reconcile/ExceptionDashboard").then(
		(m) => ({ default: m.ExceptionDashboard }),
	),
);
const MappingRulesPage = lazyWithRetry(() =>
	import("../pages/company/advanced_reconcile/MappingRulesPage").then((m) => ({
		default: m.MappingRulesPage,
	})),
);
const Reconciliation = lazyWithRetry(() =>
	import("../pages/company/advanced_reconcile/Reconciliation").then((m) => ({
		default: m.Reconciliation,
	})),
);
const ReconciliationDashboard = lazyWithRetry(() =>
	import("../pages/company/advanced_reconcile/ReconciliationDashboard").then(
		(m) => ({ default: m.ReconciliationDashboard }),
	),
);
const ReconciliationHistoryTable = lazyWithRetry(() =>
	import("../pages/company/advanced_reconcile/ReconciliationHistoryTable").then(
		(m) => ({ default: m.ReconciliationHistoryTable }),
	),
);
const ReconciliationResultsPage = lazyWithRetry(
	() => import("../pages/company/advanced_reconcile/ReconciliationResults"),
);
const ReconciliationRules = lazyWithRetry(() =>
	import("../pages/company/advanced_reconcile/ReconciliationRules").then(
		(m) => ({ default: m.ReconciliationRules }),
	),
);
const RiskFindingsPage = lazyWithRetry(() =>
	import("../pages/company/advanced_reconcile/RiskFindingsPage").then((m) => ({
		default: m.RiskFindingsPage,
	})),
);
const ScheduleHealthDashboard = lazyWithRetry(() =>
	import("../pages/company/advanced_reconcile/ScheduleHealthDashboard").then(
		(m) => ({ default: m.ScheduleHealthDashboard }),
	),
);
const SchedulesManagementPage = lazyWithRetry(() =>
	import("../pages/company/advanced_reconcile/SchedulesManagementPage").then(
		(m) => ({ default: m.SchedulesManagementPage }),
	),
);
const RecHistory = lazyWithRetry(() =>
	import("../pages/company/classic_reconcile/History").then((m) => ({
		default: m.RecHistory,
	})),
);
const RecResult = lazyWithRetry(() =>
	import("../pages/company/classic_reconcile/Result").then((m) => ({
		default: m.RecResult,
	})),
);
const RecRun = lazyWithRetry(() =>
	import("../pages/company/classic_reconcile/Run").then((m) => ({
		default: m.RecRun,
	})),
);
const EcommerceHome = lazyWithRetry(() =>
	import("../pages/company/ecommerce/EcommerceHome").then((m) => ({
		default: m.EcommerceHome,
	})),
);
const EcommerceStore = lazyWithRetry(() =>
	import("../pages/company/ecommerce/EcommerceStore").then((m) => ({
		default: m.EcommerceStore,
	})),
);
const ShopifyConnectionPage = lazyWithRetry(() =>
	import("../pages/connection/ShopifyConnection").then((m) => ({
		default: m.ShopifyConnectionPage,
	})),
);
const ShopifyInstallPage = lazyWithRetry(() =>
	import("../pages/connection/ShopifyInstall").then((m) => ({
		default: m.ShopifyInstallPage,
	})),
);
const ProjectTasksPage = lazyWithRetry(() =>
	import("../pages/project-management/ProjectTasks").then((m) => ({
		default: m.ProjectTasksPage,
	})),
);

/**
 * `/firm` -> the close tracker, keeping the query string.
 *
 * The firm digest deep-links `/firm?company=N&period=YYYY-MM`. Nothing reads
 * those params today (the old board did not either), but dropping them on the
 * way past would make a later fix impossible for every link already sitting in
 * somebody's inbox.
 */
function FirmCloseRedirect() {
	const location = useLocation();
	return <Navigate to={`/workflow${location.search}`} replace />;
}

// Component to redirect while preserving search params
function ReconcileRedirect() {
	const location = useLocation();
	return <Navigate to={`dashboard${location.search}`} replace />;
}

export function AppRoutes() {
	return (
		<Router>
			{/* One boundary for the whole tree: each lazy page suspends only while
			    its chunk is in flight, so this shows the app loader on navigation
			    to a not-yet-fetched route. */}
			<Suspense fallback={<FullLoader />}>
				<Routes>
					<Route path="signin" element={<SignInPage />} />
					<Route path="signup" element={<SignUpPage />} />
					<Route path="appsumo" element={<AppSumo />} />
					<Route path="verify-email" element={<VerifyEmailPage />} />
					<Route path="verify-change-email" element={<VerifyEmailPage />} />
					<Route path="forgot" element={<ForgotPasswordPage />} />
					<Route path="reset" element={<ResetPasswordPage />} />
					<Route path="connection/qbo" element={<QboConnectionPage />} />
					<Route path="reconcile" element={<QboReconcileRedirector />} />{" "}
					{/* Added new top-level reconcile route */}
					<Route
						path="quickbooks/disconnected"
						element={<QboDisconnectHandler />}
					/>
					<Route
						path="connection/shopify"
						element={<ShopifyConnectionPage />}
					/>
					{/* Amazon's OAuth redirect chain ends here. Public, like the QBO and
				    Shopify callbacks: the popup has no session and the connection was
				    already created by the API before this route is reached. */}
					<Route path="connection/amazon" element={<AmazonConnectionPage />} />
					<Route path="shopify/install" element={<ShopifyInstallPage />} />
					<Route path="/" element={<Protected />}>
						<Route index element={<AllCompaniesPage />} />
						<Route path="admin">
							<Route index element={<Navigate to="/admin/users" replace />} />
							<Route path="users" element={<AdminUsersPage />} />
							<Route
								path="support-access"
								element={<AdminSupportAccessPage />}
							/>
							<Route path="companies" element={<AdminCompaniesPage />} />
							<Route path="staff-access" element={<StaffAccessPage />} />
							{/* The Access Log moved here from company settings: the
							    audience is staff auditing privileged access across every
							    customer, not a customer reading their own history. */}
							<Route path="access-log" element={<AdminAccessLogPage />} />
							<Route path="rules" element={<AdminRulesPage />} />
							<Route path="services" element={<AdminServicesPage />} />
							<Route path="machine-learning" element={<MachineLearningAdmin />}>
								<Route index element={<MLAdminDashboard />} />
								<Route path="tenants" element={<TenantManagement />} />
								<Route path="models" element={<ModelManagement />} />
								<Route path="training" element={<Training />} />
								<Route path="configuration" element={<SystemConfiguration />} />
								<Route path="data" element={<DataManagement />} />
								<Route path="monitoring" element={<Monitoring />} />
							</Route>
							<Route path="chat" element={<ChatAdmin />}>
								<Route index element={<General />} />
								<Route path="chat-interface" element={<Chat />} />
								<Route path="sessions" element={<Sessions />} />
								{/* Live staff handoff. API gates on SUPPORT_SESSIONS. */}
								<Route path="live" element={<LiveQueue />} />
								<Route path="live/:handoffId" element={<LiveHandoffDetail />} />
								<Route path="sources" element={<Sources />} />
								<Route path="mcp-tools" element={<McpTools />} />
								<Route path="prompt-templates" element={<PromptTemplates />} />
								<Route path="schemas" element={<Schemas />} />
								<Route path="guardrails" element={<Guardrails />} />
								<Route path="analytics" element={<Analytics />} />
							</Route>
							<Route path="file-converter" element={<FileManagerTab />} />
							<Route
								path="credit-packages"
								element={<AdminCreditPackagesPage />}
							/>
							<Route path="support-tickets" element={<SupportTicketsAdmin />}>
								<Route index element={<SupportTicketDashboard />} />
								<Route path="tickets" element={<TicketList />} />
								<Route path="tickets/:id" element={<TicketDetail />} />
								<Route path="categories" element={<Categories />} />
								<Route path="macros" element={<SupportMacrosPage />} />
								<Route path="sla-config" element={<SlaConfigPage />} />
								<Route path="auto-close" element={<AutoCloseSettingsPage />} />
								<Route path="spam-settings" element={<SpamSettingsPage />} />
							</Route>
						</Route>
						<Route path="account" element={<AccountPage />}>
							<Route index element={<PersonalPage />} />
							<Route path="billing" element={<BillingPage />} />
							<Route path="credits" element={<CreditsPage />} />
						</Route>
						<Route path="assistant" element={<AssistantPage />} />
						<Route path="team" element={<TeamPage />} />
						{/*
						  R1.2: `/firm` opens the close tracker.
						  A REDIRECT rather than a second mount of the same component, so
						  there is exactly one canonical URL for the close. Old bookmarks,
						  the sidebar and the firm digest's `/firm?company=N&period=…`
						  links all keep resolving instead of 404ing.
						  🪤 `FirmCloseBoardPage` is now UNROUTED and therefore dead code
						  by ADR 0014's own standard. Deleting it (and CloseRosterTable,
						  FirmCloseHero, AttentionStrip, FirmClientCard, FirmCloseBoardView)
						  is a follow-up, kept separate so this change stays one line to
						  revert. `ClientClosePanel` survives: it is what
						  `/workflow/client/:companyId` mounts.
						*/}
						{/*
						  The close tracker sits at exactly `/workflow`, ABOVE
						  `workflow/:tab` — an index-less path the tab route never
						  matched, so today's Projects / Tasks / Templates / Workflows
						  tabs keep their URLs unchanged.

						  These three are nested under `CloseTrackerGate`, which hides
						  them when `hide_dashboard` is on — the same flag that hides the
						  sidebar entry pointing here (`AppMenu.tsx`), so the link and the
						  URL turn off together instead of the page staying live for
						  anyone holding a bookmark or a firm-digest email. A pathless
						  parent does not change route ranking, so `workflow/:tab` below
						  still matches exactly what it did before.
						*/}
						<Route element={<CloseTrackerGate />}>
							<Route path="firm" element={<FirmCloseRedirect />} />
							{/*
							  EQ-2317 moved to the Close Books template library. Old
							  links and bookmarks land on the new page.
							*/}
							<Route
								path="firm/templates"
								element={<Navigate to="/workflow/close/templates" replace />}
							/>
							{/*
							  Three segments, so it never collides with `workflow/:tab`.
							  This is where the derived facts, the per-client step overrides
							  and the close schedule live now — previously reachable only
							  inside an expanded row on `/firm`.
							*/}
							<Route
								path="workflow/client/:companyId"
								element={<ClientCloseSettingsPage />}
							/>
							{/*
							  The old close address. A close opens as its project
							  (`workflow/projects/:id`) now, so both forms find the
							  close and forward there; a period with no close goes to
							  the board with setup open. `workflow/close` is a static
							  segment, so it outranks `workflow/:tab` below.
							*/}
							<Route
								path="workflow/close"
								element={<Navigate to="/workflow" replace />}
							/>
							<Route
								path="workflow/close/:companyId"
								element={<CloseAddressResolver />}
							/>
							<Route
								path="workflow/close/:companyId/:period"
								element={<CloseAddressResolver />}
							/>
						</Route>
						{/*
						  Intercompany is NOT behind the gate: it is its own page, and
						  only its sidebar position (a child of the close entry) ties it
						  to the close at all. It has its own flag, `hide_intercompany`,
						  which also hides its sidebar entry.
						*/}
						<Route element={<IntercompanyGate />}>
							<Route path="firm/intercompany" element={<IntercompanyPage />} />
						</Route>
						{/*
						  Two close addresses that change owner with
						  `hide_pm_close_tabs`, so they sit outside `CloseTrackerGate`
						  and `PmCloseTabsRoute` applies the same `hide_dashboard` rule
						  while the flag is on. A pathless parent never changed route
						  ranking, so moving them changes nothing else.

						  `/workflow`: the Close Tracker page today; with the flag off,
						  the Close board tab (also for the old `?view=week`) of Project
						  Management, and the Projects tab when `hide_dashboard` hides
						  the close.

						  `workflow/close/templates`: the team's close template
						  library. A static segment, so it outranks
						  `workflow/close/:companyId` (which would otherwise read
						  "templates" as a company id) and never reaches
						  `workflow/:tab`. With the flag off it is the Templates tab,
						  and `hide_dashboard` still sends it home.
						*/}
						<Route
							path="workflow"
							element={
								<PmCloseTabsRoute
									legacy={<CloseTrackerPage />}
									tabs={<ProjectManagementPage />}
								/>
							}
						/>
						<Route
							path="workflow/close/templates"
							element={
								<PmCloseTabsRoute
									legacy={<CloseTemplatesPage />}
									tabs={<Navigate to="/workflow/templates" replace />}
									isCloseSurface
								/>
							}
						/>
						<Route path="workflow/:tab" element={<ProjectManagementPage />} />
						<Route
							path="workflow/projects/:id"
							element={<ProjectTasksPage />}
						/>
						<Route path="document-center" element={<DocumentCenterPage />} />
						<Route path="help" element={<HelpPage />} />
						<Route path="file-converter" element={<FileConverterPage />} />
						<Route path="support" element={<SupportPage />} />
						<Route path="support/:id" element={<TicketView />} />
						<Route path="company/:id" element={<CompanyPage />}>
							<Route index element={<AnalyticsPage />} />
							<Route path="analytics" element={<AnalyticsPage />} />
							<Route path="dashboard" element={<DashboardPage />} />
							<Route path="review" element={<ReviewPage />}>
								<Route index element={<ReviewRun />} />
								<Route path="run" element={<ReviewRun />} />
								{/*<Route path="rules" element={<ReviewRules />} />*/}
								<Route path="rules/:id" element={<ReviewRule />} />
								<Route path="history" element={<ReviewHistory />} />
								<Route path="result/:id" element={<ReviewResult />} />
								<Route path="result" element={<ReviewResult />} />
							</Route>
							<Route path="reconciles" element={<Reconciliation />}>
								<Route index element={<ReconcileRedirect />} />
								<Route path="dashboard" element={<ReconciliationDashboard />} />
								<Route
									path="results/*"
									element={<ReconciliationResultsPage />}
								/>
								<Route path="rules" element={<ReconciliationRules />} />
								<Route path="mapping-rules" element={<MappingRulesPage />} />
								<Route
									path="mapping-rules/:ruleId"
									element={<MappingRulesPage />}
								/>
								<Route path="exception" element={<ExceptionDashboard />} />
								<Route path="risk-findings" element={<RiskFindingsPage />} />
								<Route
									path="history"
									element={<ReconciliationHistoryTable />}
								/>
								<Route path="schedules" element={<SchedulesManagementPage />} />
								<Route
									path="schedule-health"
									element={<ScheduleHealthDashboard />}
								/>
							</Route>
							<Route path="reconcile" element={<ReconcilePage />}>
								<Route index element={<RecRun />} />
								<Route path="history" element={<RecHistory />} />
								<Route path="result/:id" element={<RecResult />} />
								<Route path="result" element={<RecResult />} />
							</Route>
							<Route path="reclassify" element={<ReclassifyPage />} />
							<Route path="ecommerce" element={<EcommerceHome />} />
							<Route
								path="ecommerce/stores/:connectionId"
								element={<EcommerceStore />}
							/>
							<Route path="services" element={<ServicesLayout />}>
								<Route index element={<ServicesPage />} />
								<Route path="grant-access" element={<GrantAccess />} />
								<Route path="document-access" element={<DocumentsAccess />} />
								<Route path="schedule-meeting" element={<ScheduleMeeting />} />
								<Route
									path="service-completion"
									element={<ServiceCompletionPage />}
								/>
							</Route>
							<Route path="files" element={<FilesPage />} />
							<Route path="settings/*" element={<SettingsPage />} />
						</Route>
					</Route>
					<Route path="*" element={<Navigate replace to="/" />} />
				</Routes>
			</Suspense>
		</Router>
	);
}
